import { useState } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, Lock, CheckCircle, AlertCircle, Rocket } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { useData } from '../context/DataContext';
import { parseExcelToSiteData } from '../utils/excelParser';
import UserMenu from './UserMenu';

interface AdminPanelProps {
  isAuthenticated: boolean;
  onAuthenticate: (password: string) => void;
  onLogout: () => void;
  onNavigateToDashboard: () => void;
}

export const AdminPanel = ({ isAuthenticated, onAuthenticate, onLogout, onNavigateToDashboard }: AdminPanelProps) => {
  const [password, setPassword] = useState('');
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const { toast } = useToast();
  const { uploadedFile, isDataPublished, uploadFile, publishData } = useData();

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    onAuthenticate(password);
    setPassword('');
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setUploadStatus('error');
      toast({
        title: "Invalid File Type",
        description: "Please upload an Excel file (.xlsx or .xls)",
        variant: "destructive"
      });
      return;
    }

    setUploadStatus('uploading');
    
    try {
      const parseResult = await parseExcelToSiteData(file);
      
      if (parseResult.warnings.length > 0) {
        toast({
          title: "Data Warnings",
          description: `${parseResult.warnings.length} rows had missing data. Check console for details.`,
          variant: "destructive"
        });
        console.warn('Excel parsing warnings:', parseResult.warnings);
      }
      
      uploadFile(file, parseResult.data);
      setUploadStatus('success');
      toast({
        title: "Upload Successful",
        description: `File "${file.name}" processed successfully. Found ${parseResult.data.length} valid sites.`,
      });
      
      // Reset status after 3 seconds
      setTimeout(() => setUploadStatus('idle'), 3000);
    } catch (error) {
      setUploadStatus('error');
      toast({
        title: "Upload Failed",
        description: error instanceof Error ? error.message : "Failed to process the Excel file.",
        variant: "destructive"
      });
    }
  };

  const handlePublishData = () => {
    publishData();
    toast({
      title: "Data Published",
      description: "Dashboard has been updated with the new data.",
    });
    // Auto-redirect to dashboard after publishing
    setTimeout(() => {
      onNavigateToDashboard();
    }, 1500);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <Card className="w-full max-w-md p-8 shadow-elevated">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-primary-foreground" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">Admin Access</h1>
            <p className="text-muted-foreground mt-2">Enter password to access admin panel</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="text-center"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-gradient-primary hover:opacity-90"
              disabled={!password.trim()}
            >
              Access Admin Panel
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Admin Panel</h1>
            <p className="text-muted-foreground">Manage MentorLearn Cloud Portal data</p>
          </div>
          <div className="flex items-center gap-2">
            <UserMenu />
            <Button onClick={onLogout} variant="outline">
              Logout
            </Button>
          </div>
        </div>

        {/* Upload Section */}
        <Card className="p-8 shadow-card">
          <div className="text-center space-y-6">
            <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
              <Upload className="w-10 h-10 text-primary" />
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-foreground mb-2">Upload Excel Data</h2>
              <p className="text-muted-foreground">
                Upload a new Excel file to update the dashboard with latest site statistics
              </p>
            </div>

            <div className="max-w-md mx-auto">
              <Label htmlFor="excel-file" className="block text-center">
                <div 
                  className="border-2 border-dashed border-border rounded-lg p-8 hover:border-primary/50 transition-colors cursor-pointer"
                  onDrop={(e) => {
                    e.preventDefault();
                    const files = Array.from(e.dataTransfer.files);
                    const excelFile = files.find(file => 
                      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                      file.type === 'application/vnd.ms-excel'
                    );
                    if (excelFile) {
                      // Create a proper file input with the dropped file
                      const fileInput = document.getElementById('excel-file') as HTMLInputElement;
                      if (fileInput) {
                        const dataTransfer = new DataTransfer();
                        dataTransfer.items.add(excelFile);
                        fileInput.files = dataTransfer.files;
                        const event = new Event('change', { bubbles: true });
                        Object.defineProperty(event, 'target', { value: fileInput });
                        handleFileUpload(event as unknown as React.ChangeEvent<HTMLInputElement>);
                      }
                    }
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = 'hsl(var(--primary))';
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = '';
                  }}
                >
                  <Input
                    id="excel-file"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="space-y-2">
                    <Upload className="w-8 h-8 text-muted-foreground mx-auto" />
                    <p className="text-sm text-muted-foreground">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Excel files (.xlsx, .xls) only
                    </p>
                  </div>
                </div>
              </Label>
            </div>

            {/* Upload Status */}
            {uploadStatus === 'uploading' && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Uploading and processing file...
                </AlertDescription>
              </Alert>
            )}

            {uploadStatus === 'success' && (
              <Alert className="border-success text-success">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  File uploaded successfully! Dashboard data has been updated.
                </AlertDescription>
              </Alert>
            )}

            {uploadStatus === 'error' && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Upload failed. Please check file format and try again.
                </AlertDescription>
              </Alert>
            )}

            {/* Publish Button */}
            {uploadedFile && !isDataPublished && uploadStatus === 'idle' && (
              <div className="mt-6 p-4 bg-accent/50 rounded-lg border border-accent">
                <div className="text-center space-y-3">
                  <div className="flex items-center justify-center gap-2 text-accent-foreground">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">File Ready: {uploadedFile.name}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Click publish to update the dashboard with the new data
                  </p>
                  <Button 
                    onClick={handlePublishData}
                    className="bg-gradient-primary hover:opacity-90"
                    size="lg"
                  >
                    <Rocket className="w-4 h-4 mr-2" />
                    Publish to Dashboard
                  </Button>
                </div>
              </div>
            )}

            {/* Published Status */}
            {isDataPublished && (
              <Alert className="border-success text-success bg-success/10">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  Data has been published to the dashboard successfully!
                </AlertDescription>
              </Alert>
            )}
          </div>
        </Card>

        {/* Data Info */}
        <Card className="p-6 shadow-card">
          <h3 className="text-lg font-semibold mb-4">Expected Excel Columns</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
            <div>• SiteName</div>
            <div>• IsOnline</div>
            <div>• Customer</div>
            <div>• Country</div>
            <div>• Region</div>
            <div>• SiteType</div>
            <div>• MLVersion</div>
            <div>• DBVersion</div>
            <div>• SiteInstallDate</div>
            <div>• SiteLicenseExpiration</div>
            <div>• StartUsage</div>
            <div>• LastUsage</div>
            <div>• NumSessionsLastMonth</div>
            <div>• NumSessionsOnServerLast12Month</div>
          </div>
        </Card>
      </div>
    </div>
  );
};